/**
 * GalleryImagesCardV3 — Sidebar card for the V3 editor.
 *
 * Lets the vendor upload contextual / lifestyle images (usage shots, packaging,
 * etc.) for the default variant. Shows images already hosted on the CDN side-
 * by-side with files picked in the current session (not yet uploaded). New
 * files are persisted on the next product submission via useProductSubmissionV3.
 *
 * Also owns the cover image — `visualization.thumbnailPath`, the picture the
 * catalogue and the visualizer show for this variant. Until now it was only
 * ever set by whatever generated it, and there was no way to change it by
 * hand: the editor displayed the thumbnail in two places and offered no way to
 * replace it.
 *
 * A file chosen here becomes a base64 data URL rather than an upload of its
 * own, because useProductSubmissionV3 already recognises that and turns it
 * into a real file on submit. One upload path, not two.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { CloudUpload, Images, Plus, X } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card } from '../shared/cards/Card';
import type { VariantV3 } from '../../types';

interface GalleryImagesCardV3Props {
  /** Variant to edit — gallery images live per-variant. */
  variant: VariantV3 | null;
  /**
   * Patch the variant.
   *
   * `visualization` is passed whole rather than as a path, because that is the
   * shape the form state holds and a partial write would drop the rest of the
   * block.
   */
  onChange: (patch: {
    galleryImages?: string[];
    _galleryUploads?: File[];
    visualization?: VariantV3['visualization'];
  }) => void;
}

const MAX_GALLERY_IMAGES = 12;

interface GalleryItem {
  key: string;
  kind: 'url' | 'file';
  preview: string;
  index: number;
}

export const GalleryImagesCardV3: React.FC<GalleryImagesCardV3Props> = ({
  variant,
  onChange,
}) => {
  const { t } = useTranslation();
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const [coverBusy, setCoverBusy] = useState(false);
  const coverInputRef = React.useRef<HTMLInputElement>(null);
  const coverPath = variant?.visualization?.thumbnailPath;

  /**
   * Read the file here rather than uploading it now.
   *
   * The submission pipeline converts a base64 thumbnailPath into a file on
   * save, so a picture chosen and then abandoned never reaches storage — and
   * the vendor can change their mind without leaving an orphan behind.
   */
  const chooseCover = useCallback(
    (file: File) => {
      if (!file.type.startsWith('image/')) return;

      setCoverBusy(true);
      const reader = new FileReader();
      reader.onload = () => {
        onChange({
          visualization: {
            ...(variant?.visualization ?? {}),
            thumbnailPath: String(reader.result),
          } as VariantV3['visualization'],
        });
        setCoverBusy(false);
      };
      reader.onerror = () => setCoverBusy(false);
      reader.readAsDataURL(file);
    },
    [onChange, variant?.visualization],
  );

  /**
   * Promote a gallery image to the cover — no upload, it is already hosted.
   *
   * Not named `useAsCover`: anything starting with `use` is read as a hook by
   * the rules-of-hooks lint, and calling it from an onClick then fails the
   * build.
   */
  const makeCover = useCallback(
    (url: string) => {
      onChange({
        visualization: { ...(variant?.visualization ?? {}), thumbnailPath: url } as VariantV3['visualization'],
      });
    },
    [onChange, variant?.visualization],
  );

  const existingUrls = variant?.galleryImages ?? [];
  const pendingFiles = variant?._galleryUploads ?? [];
  const totalCount = existingUrls.length + pendingFiles.length;

  // Close lightbox with Escape, navigate with arrow keys
  useEffect(() => {
    if (lightboxIndex === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightboxIndex(null);
      if (e.key === 'ArrowRight')
        setLightboxIndex((i) => (i === null ? null : Math.min(i + 1, totalCount - 1)));
      if (e.key === 'ArrowLeft')
        setLightboxIndex((i) => (i === null ? null : Math.max(i - 1, 0)));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxIndex, totalCount]);

  const items = useMemo<GalleryItem[]>(() => {
    const urls: GalleryItem[] = existingUrls.map((url, i) => ({
      key: `url-${i}-${url}`,
      kind: 'url',
      preview: url,
      index: i,
    }));
    const files: GalleryItem[] = pendingFiles.map((file, i) => ({
      key: `file-${i}-${file.name}-${file.lastModified}`,
      kind: 'file',
      preview: URL.createObjectURL(file),
      index: i,
    }));
    return [...urls, ...files];
  }, [existingUrls, pendingFiles]);

  const handleDrop = useCallback(
    (accepted: File[]) => {
      if (accepted.length === 0) return;
      const capacity = Math.max(0, MAX_GALLERY_IMAGES - totalCount);
      const added = accepted.slice(0, capacity);
      if (added.length === 0) return;
      onChange({ _galleryUploads: [...pendingFiles, ...added] });
    },
    [pendingFiles, totalCount, onChange]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    accept: { 'image/*': ['.png', '.jpg', '.jpeg', '.webp'] },
    multiple: true,
  });

  const removeUrl = (index: number) => {
    const next = existingUrls.filter((_, i) => i !== index);
    onChange({ galleryImages: next });
  };

  const removeFile = (index: number) => {
    const next = pendingFiles.filter((_, i) => i !== index);
    onChange({ _galleryUploads: next });
  };

  const cardTitle = t('PAGES.NEW_PRODUCT.V3.GALLERY.TITLE', 'Gallery Images');

  if (!variant) {
    return (
      <Card title={cardTitle}>
        <p className="text-sm text-muted-foreground">
          {t('PAGES.NEW_PRODUCT.V3.GALLERY.NO_VARIANT', 'Select a variant to manage gallery images.')}
        </p>
      </Card>
    );
  }

  return (
    <Card title={cardTitle}>
      {/*
        * The cover image, above the gallery.
        *
        * First because it is the picture that does the most work — the one the
        * catalogue and the visualizer show — and because a vendor looking for
        * "the product image" looks at the top of the imagery card, not past a
        * grid of lifestyle shots.
        */}
      <div className="mb-4 flex items-start gap-3">
        <div
          onClick={() => coverInputRef.current?.click()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') coverInputRef.current?.click();
          }}
          className={cn(
            'flex size-[72px] shrink-0 cursor-pointer items-center justify-center overflow-hidden border bg-muted transition-colors hover:border-foreground/30',
            coverBusy && 'opacity-50',
          )}
        >
          {coverPath ? (
            <img
              src={coverPath}
              alt={t('PAGES.NEW_PRODUCT.V3.COVER.TITLE', 'Cover image') as string}
              className="size-full object-cover"
            />
          ) : (
            <Plus className="size-5 text-muted-foreground" />
          )}
        </div>

        <input
          ref={coverInputRef}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) chooseCover(file);
            // Reset so picking the same file twice still fires.
            e.target.value = '';
          }}
        />

        <div className="min-w-0">
          <p className="text-sm font-medium text-foreground">
            {t('PAGES.NEW_PRODUCT.V3.COVER.TITLE', 'Cover image')}
          </p>
          <p className="text-sm leading-relaxed text-muted-foreground">
            {coverPath
              ? t('PAGES.NEW_PRODUCT.V3.COVER.REPLACE', 'Shown in the catalogue and the visualizer. Click to replace it.')
              : t('PAGES.NEW_PRODUCT.V3.COVER.EMPTY', 'Shown in the catalogue and the visualizer. Click to choose one.')}
          </p>

          {existingUrls.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {existingUrls.slice(0, 6).map((url) => (
                <img
                  key={url}
                  src={url}
                  alt=""
                  onClick={() => makeCover(url)}
                  title={t('PAGES.NEW_PRODUCT.V3.COVER.USE_THIS', 'Use this as the cover') as string}
                  className={cn(
                    'size-7 cursor-pointer border object-cover',
                    url === coverPath && 'border-foreground',
                  )}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <p className="mb-3 text-sm text-muted-foreground">
        {t(
          'PAGES.NEW_PRODUCT.V3.GALLERY.DESCRIPTION',
          'Contextual shots (usage, packaging, lifestyle). Shown as a gallery on the product page. Does not affect the visualizer thumbnail.'
        )}
      </p>

      {totalCount === 0 ? (
        <div
          {...getRootProps()}
          className={cn(
            'cursor-pointer border-2 border-dashed p-6 text-center transition-colors hover:border-foreground hover:bg-accent/40',
            isDragActive ? 'border-foreground bg-accent/40' : 'border-input bg-background',
          )}
        >
          <input {...getInputProps()} />
          <div className="mx-auto mb-3 flex size-14 items-center justify-center rounded-full bg-muted">
            <CloudUpload className="size-7 text-muted-foreground" />
          </div>
          <p className="mb-0.5 text-sm font-medium text-foreground">
            {isDragActive
              ? t('PAGES.NEW_PRODUCT.V3.GALLERY.DROP_HERE', 'Drop images here')
              : t('PAGES.NEW_PRODUCT.V3.GALLERY.UPLOAD_CTA', 'Upload gallery images')}
          </p>
          <p className="mb-3 text-sm text-muted-foreground">
            {t('PAGES.NEW_PRODUCT.V2.PLANK_UPLOADER.DRAG_DROP_OR_BROWSE', 'Drag & drop or click to browse')}
          </p>
          <div className="inline-flex items-center gap-1 border bg-muted px-3 py-1">
            <Images className="size-3.5 text-muted-foreground" />
            <span className="text-xs text-muted-foreground">
              {t('PAGES.NEW_PRODUCT.V3.GALLERY.FILE_TYPES', 'PNG, JPG, WEBP · up to {{max}}', { max: MAX_GALLERY_IMAGES })}
            </span>
          </div>
        </div>
      ) : (
        <div className="border bg-muted p-3">
          <div className="flex flex-wrap gap-2.5">
            {items.map((item, globalIndex) => (
              <div
                key={item.key}
                onClick={() => setLightboxIndex(globalIndex)}
                className={cn(
                  'group/thumb relative size-[72px] cursor-zoom-in overflow-hidden border bg-background transition-transform hover:scale-105',
                  item.kind === 'file' && 'border-foreground',
                )}
              >
                <img
                  src={item.preview}
                  alt={`Gallery ${item.index + 1}`}
                  className="size-full object-cover"
                />
                <Button
                  size="icon-xs"
                  variant="ghost"
                  aria-label={t('COMMON.REMOVE_IMAGE', 'Remove image') as string}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (item.kind === 'url') removeUrl(item.index);
                    else removeFile(item.index);
                  }}
                  className="absolute end-1 top-1 size-5 bg-foreground/70 text-background opacity-0 transition-opacity group-hover/thumb:opacity-100 hover:bg-destructive hover:text-background"
                >
                  <X className="size-3" />
                </Button>
                {item.kind === 'file' && (
                  <span className="absolute bottom-1 start-1 bg-foreground/65 px-1 text-[9px] leading-none font-medium text-background">
                    {t('PAGES.NEW_PRODUCT.V3.GALLERY.PENDING_BADGE', 'NEW')}
                  </span>
                )}
              </div>
            ))}

            {totalCount < MAX_GALLERY_IMAGES && (
              <div
                {...getRootProps()}
                className={cn(
                  'flex size-[72px] cursor-pointer flex-col items-center justify-center border-2 border-dashed transition-colors hover:border-foreground hover:bg-accent/40',
                  isDragActive ? 'border-foreground bg-accent/40' : 'border-input bg-background',
                )}
              >
                <input {...getInputProps()} />
                <Plus
                  className={cn(
                    'mb-0.5 size-[22px]',
                    isDragActive ? 'text-foreground' : 'text-muted-foreground',
                  )}
                />
                <span className="text-[10px] font-medium text-muted-foreground">
                  {t('COMMON.ADD', 'Add')}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Full-screen lightbox — portalled to document.body so it escapes
          the sticky sidebar's stacking context and covers the top bar / nav. */}
      {lightboxIndex !== null &&
        createPortal(
          <div
            onClick={() => setLightboxIndex(null)}
            className="fixed inset-0 z-[2000] flex flex-col items-center justify-center bg-black/90 p-8"
          >
            <div className="relative flex max-h-[88vh] max-w-[92vw] items-center justify-center">
              {items[lightboxIndex] && (
                <img
                  src={items[lightboxIndex].preview}
                  alt={`Gallery ${lightboxIndex + 1}`}
                  onClick={(e) => e.stopPropagation()}
                  className="max-h-[88vh] max-w-[92vw] object-contain"
                />
              )}

              <Button
                variant="ghost"
                size="icon-lg"
                aria-label={t('PAGES.NEW_PRODUCT.V3.GALLERY.LIGHTBOX_CLOSE', 'Close')}
                onClick={(e) => {
                  e.stopPropagation();
                  setLightboxIndex(null);
                }}
                className="fixed end-4 top-4 size-11 bg-white/10 text-white backdrop-blur-sm hover:bg-white/20 hover:text-white"
              >
                <X />
              </Button>

              {/* Counter */}
              {totalCount > 1 && (
                <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-white/10 px-4 py-1.5 backdrop-blur-sm">
                  <span className="text-sm font-medium text-white">
                    {lightboxIndex + 1} / {totalCount}
                  </span>
                </div>
              )}
            </div>
          </div>,
          document.body,
        )}
    </Card>
  );
};
